import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MapPin, Filter, Star, BadgeCheck, Users, X, ChevronDown, Send, DollarSign, FileText, CheckSquare, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import VendorMapView from '@/components/VendorMapView';
import useSeo from '@/hooks/useSeo';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrowsePage() {
  useSeo({
    title: 'Browse Indigenous Vendors · EntrepreNatives',
    description: 'Browse hundreds of verified Indigenous-owned businesses. Filter by category, location, rating, and membership tier. Find Native American artisans, First Nations service providers, and Indigenous entrepreneurs across the Americas.',
    url: 'https://entreprenatives.com/browse',
  });
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcategory') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [vendorType, setVendorType] = useState(searchParams.get('vendor_type') || '');
  const [membershipLevel, setMembershipLevel] = useState(searchParams.get('membership') || '');
  const [minRating, setMinRating] = useState(searchParams.get('rating') ? parseFloat(searchParams.get('rating')) : 0);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);

  // Book Now modal state
  const [bookModal, setBookModal] = useState(null); // vendor object or null
  const [bookServices, setBookServices] = useState('');
  const [bookBudget, setBookBudget] = useState('');
  const [requestPrice, setRequestPrice] = useState(false);
  const [bookSending, setBookSending] = useState(false);
  const [viewMode, setViewMode] = useState(searchParams.get('view') === 'map' ? 'map' : 'list');

  const handleBookNow = (e, vendor) => {
    e.preventDefault();
    e.stopPropagation();
    setBookModal(vendor);
    setBookServices('');
    setBookBudget('');
    setRequestPrice(false);
  };

  const sendBookingInquiry = async () => {
    if (!user) {
      const target = `/vendor/${bookModal?.vendor_id || ''}`;
      try { sessionStorage.setItem('postAuthRedirect', target); } catch {}
      toast.info('Create a free account to send this inquiry.');
      navigate(`/auth?mode=register&return=${encodeURIComponent(target)}`);
      return;
    }
    if (!bookServices.trim()) { toast.error('Please describe what services you need'); return; }
    if (!bookBudget.trim() && !requestPrice) { toast.error('Enter a budget or check "Request project price"'); return; }
    setBookSending(true);
    try {
      const token = localStorage.getItem('token');
      const budgetText = requestPrice ? 'Budget: Requesting project price quote' : `Budget: $${bookBudget}`;
      const content = `📋 Service Inquiry\n\nServices needed:\n${bookServices}\n\n${budgetText}\n\n— Sent via Book Now`;
      const res = await fetch(`${API}/messages/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_id: bookModal.user_id, content, booking_id: null }),
      });
      if (res.ok) {
        toast.success(`Inquiry sent to ${bookModal.business_name}! Check your messages for their response.`);
        setBookModal(null);
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to send inquiry');
      }
    } catch (e) { toast.error('Failed to send: ' + (e.message || '')); }
    finally { setBookSending(false); }
  };

  // Browse is public — no login required to view vendors. Booking + messaging are gated downstream.
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedSubcategory, location, vendorType, membershipLevel, minRating, page, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const raw = await response.json();
        const sorted = [...raw]
          .map((c) => ({
            ...c,
            subcategories: [...(c.subcategories || [])].sort((a, b) =>
              (a.name || '').localeCompare(b.name || '')
            ),
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(sorted);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') params.set('category_id', selectedCategory);
      if (selectedSubcategory && selectedSubcategory !== 'all-sub') params.set('subcategory_id', selectedSubcategory);
      if (location) params.set('location', location);
      if (vendorType && vendorType !== 'all-types') params.set('vendor_type', vendorType);
      if (membershipLevel && membershipLevel !== 'all-levels') params.set('membership_level', membershipLevel);
      if (minRating > 0) params.set('min_rating', minRating.toString());
      if (searchQuery) params.set('search_query', searchQuery);
      params.set('page', page.toString());
      params.set('limit', '20');

      const response = await fetch(`${API}/vendors/search?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setVendors(data.vendors);
        setTotalPages(data.pages);
        setTotalCount(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchVendors();
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setLocation('');
    setVendorType('');
    setMembershipLevel('');
    setMinRating(0);
    setSearchQuery('');
    setPage(1);
  };

  const currentCategory = categories.find(c => c.category_id === selectedCategory);

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-stone-300 font-outfit mb-2 text-sm">Category</label>
        <Select value={selectedCategory} onValueChange={(val) => { setSelectedCategory(val); setSelectedSubcategory(''); setPage(1); }}>
          <SelectTrigger className="input-field w-full" data-testid="filter-category">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-stone-900 border-stone-800">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {currentCategory?.subcategories?.length > 0 && (
        <div>
          <label className="block text-stone-300 font-outfit mb-2 text-sm">Subcategory</label>
          <Select value={selectedSubcategory} onValueChange={(val) => { setSelectedSubcategory(val); setPage(1); }}>
            <SelectTrigger className="input-field w-full" data-testid="filter-subcategory">
              <SelectValue placeholder="All Subcategories" />
            </SelectTrigger>
            <SelectContent className="bg-stone-900 border-stone-800">
              <SelectItem value="all-sub">All Subcategories</SelectItem>
              {currentCategory.subcategories.map(sub => (
                <SelectItem key={sub.subcategory_id} value={sub.subcategory_id}>{sub.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <label className="block text-stone-300 font-outfit mb-2 text-sm">Location</label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
          <Input 
            value={location}
            onChange={(e) => { setLocation(e.target.value); setPage(1); }}
            placeholder="City, State"
            className="input-field w-full pl-10"
            data-testid="filter-location"
          />
        </div>
      </div>

      <div>
        <label className="block text-stone-300 font-outfit mb-2 text-sm">Vendor Type</label>
        <Select value={vendorType} onValueChange={(val) => { setVendorType(val); setPage(1); }}>
          <SelectTrigger className="input-field w-full" data-testid="filter-vendor-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-stone-900 border-stone-800">
            <SelectItem value="all-types">All Types</SelectItem>
            <SelectItem value="verified">Verified Business</SelectItem>
            <SelectItem value="startup">Startup Business</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-stone-300 font-outfit mb-2 text-sm">Membership Level</label>
        <Select value={membershipLevel} onValueChange={(val) => { setMembershipLevel(val); setPage(1); }}>
          <SelectTrigger className="input-field w-full" data-testid="filter-membership">
            <SelectValue placeholder="All Levels" />
          </SelectTrigger>
          <SelectContent className="bg-stone-900 border-stone-800">
            <SelectItem value="all-levels">All Levels</SelectItem>
            <SelectItem value="elite">Elite</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-stone-300 font-outfit mb-2 text-sm">
          Minimum Rating: {minRating > 0 ? minRating.toFixed(1) : 'Any'}
        </label>
        <Slider 
          value={[minRating]} 
          onValueChange={([val]) => { setMinRating(val); setPage(1); }}
          max={5}
          step={0.5}
          className="mt-2"
          data-testid="filter-rating"
        />
      </div>

      <Button onClick={clearFilters} variant="outline" className="w-full bg-yellow-400 hover:bg-yellow-300 text-black border-none font-bold" data-testid="clear-filters">
        Clear All Filters
      </Button>
    </div>
  );

  // Show loading while checking auth (no auth required, just wait for the auth probe)
  if (authLoading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="text-stone-400 font-outfit">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="container-main">
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="font-syne text-3xl md:text-4xl font-bold text-stone-100 mb-1" data-testid="browse-title">
                  Browse Marketplace
                </h1>
                <p className="text-stone-400 font-outfit">
                  {totalCount > 0 ? `${totalCount} vendors found` : 'Discover Indigenous businesses'}
                </p>
              </div>
              {/* List ↔ Map toggle */}
              <div className="inline-flex rounded-xl border border-stone-700 bg-stone-900 p-1" data-testid="browse-view-toggle">
                <button
                  onClick={() => {
                    setViewMode('list');
                    const sp = new URLSearchParams(searchParams);
                    sp.delete('view');
                    setSearchParams(sp);
                  }}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'list' ? 'bg-yellow-400 text-black' : 'text-stone-400 hover:text-white'
                  }`}
                  data-testid="view-toggle-list"
                >
                  List
                </button>
                <button
                  onClick={() => {
                    setViewMode('map');
                    const sp = new URLSearchParams(searchParams);
                    sp.set('view', 'map');
                    setSearchParams(sp);
                  }}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'map' ? 'bg-yellow-400 text-black' : 'text-stone-400 hover:text-white'
                  }`}
                  data-testid="view-toggle-map"
                >
                  Map
                </button>
              </div>
            </div>
          </div>

          {/* Map view — renders instead of list when toggled */}
          {viewMode === 'map' ? (
            <VendorMapView categoryId={selectedCategory || 'all'} />
          ) : (
          <>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <Input 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search vendors, services..."
                  className="input-field w-full pl-12"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="btn-primary bg-yellow-400 hover:bg-yellow-300 text-black" data-testid="search-button">
                Search
              </Button>
              
              {/* Mobile Filter Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden btn-secondary" data-testid="mobile-filter-toggle">
                    <Filter className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-stone-900 border-stone-800 w-80">
                  <SheetHeader>
                    <SheetTitle className="text-stone-100 font-syne">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterContent />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </form>

          <div className="flex gap-8">
            {/* Desktop Filters Sidebar */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="card-vendor sticky top-24">
                <h3 className="font-syne text-lg font-bold text-stone-100 mb-6">Filters</h3>
                <FilterContent />
              </div>
            </aside>

            {/* Results Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="card-vendor animate-pulse">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-xl bg-stone-800"></div>
                        <div className="flex-1">
                          <div className="h-5 bg-stone-800 rounded mb-2 w-3/4"></div>
                          <div className="h-4 bg-stone-800 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : vendors.length === 0 ? (
                <div className="card-vendor text-center py-16">
                  <Users className="w-16 h-16 text-stone-700 mx-auto mb-4" />
                  <h3 className="font-syne text-xl font-bold text-stone-300 mb-2">No vendors found</h3>
                  <p className="text-stone-500 font-outfit mb-6">Try adjusting your filters or search query</p>
                  <Button onClick={clearFilters} className="btn-primary bg-yellow-400 hover:bg-yellow-300 text-black" data-testid="no-results-clear">
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {vendors.map((vendor, index) => (
                      <motion.div
                        key={vendor.vendor_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link 
                          to={`/vendor/${vendor.vendor_id}`}
                          className="card-vendor block h-full"
                          data-testid={`vendor-card-${vendor.vendor_id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 rounded-xl overflow-hidden bg-stone-800 flex-shrink-0">
                              {vendor.profile_image || vendor.user_picture ? (
                                <img 
                                  src={vendor.profile_image || vendor.user_picture} 
                                  alt={vendor.business_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-stone-600">
                                  <Users className="w-8 h-8" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-outfit font-semibold text-stone-200 truncate">
                                {vendor.business_name}
                              </h3>
                              {vendor.owner_name && (
                                <p className="text-stone-400 text-xs truncate" data-testid={`vendor-owner-${vendor.vendor_id}`}>by {vendor.owner_name}</p>
                              )}
                              {vendor.gender && (
                                <p className="text-[#C4B5FD] text-[10px] truncate uppercase tracking-wider" data-testid={`vendor-gender-${vendor.vendor_id}`}>
                                  {vendor.gender === 'non_binary' ? 'Non-Binary' : vendor.gender.charAt(0).toUpperCase() + vendor.gender.slice(1)}
                                </p>
                              )}
                              <p className="text-stone-500 text-sm truncate">{vendor.location}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                                {vendor.main_category_name && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#8B5CF6]/15 text-[#A78BFA] font-semibold border border-[#8B5CF6]/20">{vendor.main_category_name}</span>
                                )}
                                {vendor.promoted && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white font-bold shadow-[0_0_8px_rgba(245,158,11,0.3)]">{vendor.promoted_badge || 'Promoted'}</span>
                                )}
                                {vendor.out_of_commission && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 font-bold border border-yellow-500/20">Not Taking Orders</span>
                                )}
                                {vendor.membership_level === 'elite' && (
                                  <span className="badge-elite">
                                    <BadgeCheck className="w-3 h-3" /> Elite
                                  </span>
                                )}
                                {vendor.vendor_type === 'verified' && (
                                  <span className="badge-verified">
                                    <BadgeCheck className="w-3 h-3" /> Verified
                                  </span>
                                )}
                                {vendor.vendor_type === 'startup' && (
                                  <span className="badge-startup">Startup</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <p className="mt-4 text-stone-400 text-sm font-outfit line-clamp-2">
                            {vendor.description}
                          </p>

                          {/* Portfolio Thumbnails */}
                          {vendor.portfolio_thumbs?.length > 0 && (
                            <div className="flex gap-1.5 mt-3">
                              {vendor.portfolio_thumbs.map((img, i) => {
                                if (!img) return null;
                                const src = img.startsWith('http') ? img : img.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : `${process.env.REACT_APP_BACKEND_URL}/api/users/media/${img}`;
                                return (
                                  <div key={i} className="w-16 h-12 rounded-lg overflow-hidden bg-stone-800 flex-shrink-0">
                                    <img src={src} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          <div className="mt-4 pt-4 border-t border-stone-800/50 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                <span className="text-stone-300 font-mono text-sm">
                                  {vendor.rating?.toFixed(1) || 'New'}
                                </span>
                                <span className="text-stone-500 text-sm">
                                  ({vendor.review_count || 0})
                                </span>
                              </div>
                              {vendor.jobs_completed > 0 && (
                                <span className="text-stone-500 text-xs">
                                  {vendor.jobs_completed} job{vendor.jobs_completed !== 1 ? 's' : ''}
                                </span>
                              )}
                              {vendor.badge_rising_star && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-bold border border-amber-500/20">Rising Star</span>
                              )}
                            </div>
                            {vendor.starting_price && (
                              <span className="text-stone-400 font-mono text-sm">
                                From ${vendor.starting_price}
                              </span>
                            )}
                          </div>
                          <div className="mt-3 flex gap-2">
                            <button
                              onClick={(e) => handleBookNow(e, vendor)}
                              disabled={vendor.out_of_commission}
                              className={`flex-1 py-2.5 rounded-xl text-sm font-bold font-outfit flex items-center justify-center gap-1.5 transition-all ${vendor.out_of_commission ? 'bg-white/5 text-[#64748B] cursor-not-allowed' : 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]'}`}
                              data-testid={`book-now-${vendor.vendor_id}`}
                            >
                              <Send size={13} /> {vendor.out_of_commission ? 'Unavailable' : 'Book Now'}
                            </button>
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/messages/${vendor.user_id}`); }}
                              className="py-2.5 px-4 rounded-xl border border-white/10 text-[#A78BFA] text-sm font-bold font-outfit hover:bg-white/5 transition-all flex items-center gap-1.5"
                              data-testid={`message-${vendor.vendor_id}`}
                            >
                              <MessageSquare size={13} /> Message
                            </button>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-12">
                      <Button 
                        variant="outline" 
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="btn-secondary"
                        data-testid="pagination-prev"
                      >
                        Previous
                      </Button>
                      <span className="flex items-center px-4 text-stone-400 font-mono">
                        {page} / {totalPages}
                      </span>
                      <Button 
                        variant="outline" 
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="btn-secondary"
                        data-testid="pagination-next"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </main>

      <Footer />

      {/* Book Now Modal */}
      <AnimatePresence>
        {bookModal && (
          <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBookModal(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-[#1A1A3E] border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="book-now-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-stone-800 flex-shrink-0">
                    {bookModal.profile_image || bookModal.user_picture ? (
                      <img src={bookModal.profile_image || bookModal.user_picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-600"><Users className="w-6 h-6" /></div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-white font-syne font-bold text-lg">{bookModal.business_name}</h3>
                    <p className="text-[#9CA3AF] text-xs">{bookModal.location}</p>
                  </div>
                </div>
                <button onClick={() => setBookModal(null)} className="text-[#64748B] hover:text-white transition-colors"><X size={20} /></button>
              </div>

              {/* Services needed */}
              <div className="space-y-4">
                <div>
                  <Label className="text-[#9CA3AF] text-xs mb-1.5 flex items-center gap-1.5"><FileText size={12} /> What services do you need?</Label>
                  <Textarea
                    value={bookServices}
                    onChange={(e) => setBookServices(e.target.value)}
                    placeholder="Describe the services you're looking for... e.g. 'I need a custom beaded necklace for a wedding gift' or 'Logo design for my business'"
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#4B5563] rounded-xl min-h-[100px] text-sm"
                    data-testid="book-services-input"
                  />
                </div>

                {/* Budget */}
                <div>
                  <Label className="text-[#9CA3AF] text-xs mb-1.5 flex items-center gap-1.5"><DollarSign size={12} /> Your Budget</Label>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">$</span>
                      <Input
                        type="number"
                        value={bookBudget}
                        onChange={(e) => setBookBudget(e.target.value)}
                        placeholder="Amount"
                        disabled={requestPrice}
                        className={`bg-white/5 border-white/10 text-white rounded-xl pl-7 ${requestPrice ? 'opacity-40' : ''}`}
                        data-testid="book-budget-input"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer group" data-testid="request-price-label">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${requestPrice ? 'bg-[#8B5CF6] border-[#8B5CF6]' : 'border-white/20 group-hover:border-white/40'}`} onClick={() => setRequestPrice(!requestPrice)}>
                      {requestPrice && <CheckSquare size={14} className="text-white" />}
                    </div>
                    <span className="text-[#9CA3AF] text-xs font-outfit">Request project price</span>
                  </label>
                </div>

                {/* Send Button */}
                <Button
                  onClick={sendBookingInquiry}
                  disabled={bookSending || !bookServices.trim()}
                  className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold h-12 rounded-xl mt-2"
                  data-testid="send-inquiry-btn"
                >
                  {bookSending ? 'Sending...' : <><Send size={16} className="mr-2" /> Send Inquiry</>}
                </Button>
                <p className="text-[10px] text-[#64748B] text-center">The vendor will receive your inquiry and respond via messages</p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
